import { getCurrentUserId } from "utils/clockSettings";
import { isSuperAdmin } from "utils/permissionUtils";

/** Why the server refused a payment action: what the payment became while it was on screen. */
export const PAYMENT_CONFLICT = {
  UPDATED: "PAYMENT_UPDATED",
  VERIFIED: "PAYMENT_VERIFIED",
  REJECTED: "PAYMENT_REJECTED",
  WITHDRAWN: "PAYMENT_WITHDRAWN",
};

/**
 * The conflict carried by a 409 from a payment action, or null for any other error. It holds
 * { code, message, changedByName, changedAt, rejectionFeedback, currentRevision, payment }, where
 * payment is the payment as it is now.
 */
export const getPaymentConflict = (err) => {
  const response = err?.response;
  if (response?.status !== 409) return null;
  return response?.data?.conflict ?? null;
};

/** True when the conflict means the request is no longer pending, so it has left the queue. */
export const isSettledConflict = (conflict) =>
  !!conflict && conflict.code !== PAYMENT_CONFLICT.UPDATED;

/**
 * Whether the signed-in user may edit or withdraw this payment: it must still be pending, and they
 * must be the one who sent it (a super admin may act for anyone). The server enforces the same rule.
 */
export const canChangePendingPayment = (payment) => {
  if (payment?.status !== "PENDING_VERIFICATION") return false;
  if (isSuperAdmin()) return true;
  const me = getCurrentUserId();
  return me != null && payment?.submittedById != null && String(payment.submittedById) === me;
};

/** The expectedRevision query param for verify/reject; omitted when the payment predates revisions. */
export const revisionParams = (payment) =>
  payment?.revision != null ? { expectedRevision: payment.revision } : undefined;

/**
 * The payment to show and act on: the one carried by an "updated" conflict when it is newer than the
 * copy in the list, which keeps the screen right in the gap before the refetched list arrives.
 */
export const newerPayment = (current, conflict) => {
  const fresh = conflict?.code === PAYMENT_CONFLICT.UPDATED ? conflict.payment : null;
  if (!fresh) return current;
  if (!current || current.id !== fresh.id) return current;
  return (fresh.revision ?? -1) > (current.revision ?? -1) ? { ...current, ...fresh } : current;
};
